import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import SerpContentSnapshot from '@/components/Approval/@components/SerpContentSnapshot';
import { formatSerpText } from '@/utils/approval/serpDocument';
import {
  getSerpStoreAccounts,
  isSerpAccountFieldChanged,
  isSerpStoreFieldChanged,
  SERP_STORE_CHANGE_ACCOUNT_ITEMS,
  SERP_STORE_CHANGE_ROWS,
  type SerpStoreChangeSource,
} from '@/utils/approval/serpStoreChange';
import { SerpStoreChangeRoot } from './styles';

const CHANGED_STYLE = { color: 'red' };

type TableProps = {
  source: SerpStoreChangeSource;
  t: TFunction;
};

/** 레거시 #APV_SERP_SHOP_CHANGE .form-group — 변경전과 다른 변경후 값은 빨간 글씨 */
const SerpStoreChangeTable = ({ source, t }: TableProps) => {
  const accounts = getSerpStoreAccounts(source);

  return (
    <>
      <h2 className="heading-sm font-weight-bold mt-20">
        {t('유통거래처 신규/변경')}
      </h2>
      <table className="table table-bordered mb-30 text-center">
        <colgroup>
          <col width="20%" />
          <col width="*" />
          <col width="*" />
        </colgroup>
        <thead>
          <tr>
            <th>{t('항목')}</th>
            <th>{t('변경전')}</th>
            <th>{t('변경후')}</th>
          </tr>
        </thead>
        <tbody>
          {SERP_STORE_CHANGE_ROWS.map(({ key, label }) => (
            <tr key={key}>
              <th className="bg-light">{t(label)}</th>
              <td>{formatSerpText(source[`BEF_${key}`])}</td>
              <td
                style={
                  isSerpStoreFieldChanged(source, key)
                    ? CHANGED_STYLE
                    : undefined
                }
              >
                {formatSerpText(source[`AFT_${key}`])}
              </td>
            </tr>
          ))}
          {accounts.map((account, index) => (
            <tr key={`account-${index}`} className="text-left">
              <th className="bg-light">{t('계좌{{n}}', { n: index + 1 })}</th>
              <td>
                <ul>
                  {SERP_STORE_CHANGE_ACCOUNT_ITEMS.map(({ key, label }) => (
                    <li key={key}>
                      {t(label)} :{' '}
                      <span>{formatSerpText(account[`BEF_${key}`])}</span>
                    </li>
                  ))}
                </ul>
              </td>
              <td>
                <ul>
                  {SERP_STORE_CHANGE_ACCOUNT_ITEMS.map(({ key, label }) => (
                    <li key={key}>
                      {t(label)} :{' '}
                      <span
                        style={
                          isSerpAccountFieldChanged(account, key)
                            ? CHANGED_STYLE
                            : undefined
                        }
                      >
                        {formatSerpText(account[`AFT_${key}`])}
                      </span>
                    </li>
                  ))}
                </ul>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

/** 작성 화면 표 — 저장 content 와 같은 마크업 */
export const SerpStoreChangeTableView = (props: TableProps) => (
  <SerpStoreChangeRoot>
    <SerpStoreChangeTable {...props} />
  </SerpStoreChangeRoot>
);

/** attributes.content — 레거시 조회 화면은 이 HTML 만 그대로 출력 */
export const buildSerpStoreChangeContentHtml = (
  source: SerpStoreChangeSource,
  t: TFunction,
): string =>
  renderToStaticMarkup(<SerpStoreChangeTable source={source} t={t} />);

/** 저장 content(레거시 문서 포함) 그대로 */
export const SerpStoreChangeSnapshot = ({ content }: { content: string }) => (
  <SerpStoreChangeRoot>
    <SerpContentSnapshot content={content} />
  </SerpStoreChangeRoot>
);
